"use client";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { COOKIE_BRIDGE_EVENTS } from "@/lib/cookie-bridge";
import { type TouchpointType } from "@/lib/quiz-attribution";

type ModalContent = {
  title?: string;
  description?: string;
  imageUrl?: string;
  couponCode?: string;
  type?: "banner" | "fab_icon";
  sectionType?: string;
  touchpointType?: TouchpointType;
  returnToModalId?: string;
};
interface ModalContextProps {
  modalId: string;
  handleCloseModal: () => void;
  handleOpenModal: (id: string, modalContent?: ModalContent) => void;
  modalContent?: ModalContent;
}

export const ModalContext = createContext<ModalContextProps | null>(null);

export const ModalProvider = ({ children }: React.PropsWithChildren) => {
  const [modalId, setModalId] = useState("");
  const [modalContent, setModalContent] = useState<
    ModalContextProps["modalContent"]
  >({});
  const lastDispatchedOpenRef = useRef(false);

  const handleOpenModal = (id: string, data?: ModalContent) => {
    setModalId(id);
    if (data) setModalContent(data);
  };

  const handleCloseModal = () => {
    setModalId("");
    setModalContent({});
  };

  useEffect(() => {
    const isOpen = Boolean(modalId);
    if (isOpen && !lastDispatchedOpenRef.current) {
      document.body.classList.add("cc-behind-app-modal");
      window.dispatchEvent(
        new window.CustomEvent(COOKIE_BRIDGE_EVENTS.APP_MODAL_OPEN, { detail: { modalId } })
      );
      lastDispatchedOpenRef.current = true;
    } else if (!isOpen && lastDispatchedOpenRef.current) {
      document.body.classList.remove("cc-behind-app-modal");
      window.dispatchEvent(new window.CustomEvent(COOKIE_BRIDGE_EVENTS.APP_MODAL_CLOSE));
      lastDispatchedOpenRef.current = false;
    }
  }, [modalId]);

  return (
    <ModalContext.Provider
      value={{ modalId, handleCloseModal, handleOpenModal, modalContent }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useDiscountModal must be used within a DiscountProvider");
  }
  return context as ModalContextProps;
};
